import React, { useState } from 'react';
import { useApp } from './contexts/AppContext';
import { ToastContainer } from './components/UI';
import AuthPage from './components/AuthPage';
import TaskTable from './components/TaskTable';
import KanbanBoard from './components/KanbanBoard';
import EisenhowerMatrix from './components/EisenhowerMatrix';
import OKRView from './components/OKRView';
import Planung from './components/Planung';
import WeekView from './components/WeekView';
import DayPlanning from './components/DayPlanning';
import CalendarView from './components/CalendarView';
import CanvasView from './components/CanvasView';
import Settings from './components/Settings';
import { format } from 'date-fns';
import {
  LayoutList, Columns, Grid2X2, Target, CalendarDays,
  CalendarRange, Sun, BookOpen, Settings as SettingsIcon, LogOut, Plus, Workflow
} from 'lucide-react';
import TaskModal from './components/TaskModal';
import Pomodoro from './components/Pomodoro';

const NAV_ITEMS = [
  { id: 'tasks', label: 'Tasks', icon: LayoutList },
  { id: 'kanban', label: 'Kanban', icon: Columns },
  { id: 'eisenhower', label: 'Matrix', icon: Grid2X2 },
  { id: 'okrs', label: 'OKRs', icon: Target },
  { id: 'canvas', label: 'Canvas', icon: Workflow },
  { id: 'divider1', type: 'divider' },
  { id: 'planung', label: 'Planung', icon: CalendarRange },
  { id: 'week', label: 'Woche', icon: CalendarDays },
  { id: 'day', label: 'Heute', icon: Sun },
  { id: 'calendar', label: 'Tagebuch', icon: BookOpen },
  { id: 'divider2', type: 'divider' },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
];

export default function App() {
  const { user, loading, activeView, setActiveView, logout, refreshTasks } = useApp();
  const [activeDay, setActiveDay] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [quickAdd, setQuickAdd] = useState(false);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg)' }}>
      <div style={{ fontFamily: 'var(--font-serif)', fontSize: 32, color: 'var(--accent)' }}>Work</div>
    </div>
  );

  if (!user) return <><AuthPage /><ToastContainer /></>;

  const navigateToDay = (date) => {
    setActiveDay(date);
    setActiveView('day');
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: 'var(--bg)', overflow: 'hidden' }}>
      {/* Sidebar */}
      <nav style={{
        width: 200, background: 'var(--bg-1)', borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', padding: '20px 0', flexShrink: 0,
      }}>
        {/* Logo */}
        <div style={{ padding: '0 16px 24px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 26, color: 'var(--accent)', fontWeight: 400 }}>Work</div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{user.name}</div>
        </div>

        {/* Quick Add */}
        <div style={{ padding: '12px 12px 0' }}>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setQuickAdd(true)}>
            <Plus size={14} /> Quick Add
          </button>
        </div>

        {/* Nav Items */}
        <div style={{ flex: 1, padding: '8px 8px', display: 'flex', flexDirection: 'column', gap: 2, marginTop: 8 }}>
          {NAV_ITEMS.map(item => {
            if (item.type === 'divider') return <div key={item.id} style={{ height: 1, background: 'var(--border)', margin: '6px 4px' }} />;
            const Icon = item.icon;
            const active = activeView === item.id;
            return (
              <button key={item.id}
                onClick={() => setActiveView(item.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                  borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer',
                  background: active ? 'var(--accent-dim)' : 'transparent',
                  color: active ? 'var(--accent)' : 'var(--text-2)',
                  fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: active ? 500 : 400,
                  textAlign: 'left', transition: 'all 0.1s',
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'var(--bg-3)'; e.currentTarget.style.color = 'var(--text)'; }}}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; }}}>
                <Icon size={15} />
                {item.label}
              </button>
            );
          })}
        </div>

        {/* User / Logout */}
        <div style={{ padding: '12px 12px 0', borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={logout}>
            <LogOut size={13} /> Abmelden
          </button>
        </div>
      </nav>

      {/* Main content */}
      <main style={{ flex: 1, overflow: 'auto' }}>
        {activeView === 'tasks' && <TaskTable />}
        {activeView === 'kanban' && <KanbanBoard />}
        {activeView === 'eisenhower' && <EisenhowerMatrix />}
        {activeView === 'okrs' && <OKRView />}
        {activeView === 'canvas' && <CanvasView />}
        {activeView === 'planung' && <Planung />}
        {activeView === 'week' && <WeekView onNavigateToDay={navigateToDay} />}
        {activeView === 'day' && <DayPlanning initialDate={activeDay} />}
        {activeView === 'calendar' && <CalendarView onSelectDay={navigateToDay} />}
        {activeView === 'settings' && <Settings />}
      </main>

      {/* Quick Add Modal */}
      {quickAdd && <TaskModal task={null} onClose={() => setQuickAdd(false)} onSaved={refreshTasks} />}

      <Pomodoro />
      <ToastContainer />
    </div>
  );
}
